import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["securitySelect", "addButton", "selectedSecurities", "comparisonResults"]

  connect() {
    this.selectedSecurities = []
    this.updateAddButton()
  }

  addSecurity(event) {
    event.preventDefault()

    // Get the selected value from the hidden input of the select component
    const securityId = this.getSelectedSecurityValue()
    if (!securityId) return

    // Check if security is already selected
    if (this.selectedSecurities.find(s => s.id === securityId)) {
      alert("This security is already selected for comparison.")
      return
    }

    // Check if we've reached the maximum of 5 securities
    if (this.selectedSecurities.length >= 5) {
      alert("You can only compare up to 5 securities at a time.")
      return
    }

    const securityName = this.getSelectedSecurityText()

    const security = {
      id: securityId,
      name: securityName
    }
    this.selectedSecurities.push(security)

    this.callAddCompareSecurity(securityId)

    this.updateSelectedSecuritiesList()
    this.updateComparisonResults()
    this.updateAddButton()

    this.clearSecuritySelect()
  }

  async callAddCompareSecurity(securityId) {
    // Get existing security IDs (excluding the new one being added)
    const existingSecurityIds = this.selectedSecurities.filter(s => s.id !== securityId).map(s => s.id)

    try {
      const formData = new FormData()
      formData.append('security_id', securityId)
      existingSecurityIds.forEach(id => formData.append('security_ids[]', id))

      const response = await fetch('/tools/add_compare_security', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }
    } catch (error) {
      console.error('Error adding security for comparison:', error)
    }
  }

  removeSecurity(event) {
    // Get the security ID from the button element
    const button = event.target.closest('button')
    const securityId = button.dataset.securityId
    this.selectedSecurities = this.selectedSecurities.filter(s => s.id !== securityId)

    this.callRemoveSecurity(securityId)

    this.updateSelectedSecuritiesList()
    this.updateComparisonResults()
    this.updateAddButton()
  }

  async callRemoveSecurity(securityId) {
    const remainingSecurityIds = this.selectedSecurities.map(s => s.id)

    try {
      const formData = new FormData()
      remainingSecurityIds.forEach(id => formData.append('security_ids[]', id))

      const response = await fetch('/tools/add_compare_security', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)

        // Hide comparison results if no securities remain
        if (remainingSecurityIds.length === 0) {
          const resultsContainer = this.comparisonResultsTarget
          resultsContainer.classList.add("hidden")
        }
      }
    } catch (error) {
      console.error('Error removing security from comparison:', error)
    }
  }

  removeAll() {
    this.selectedSecurities = []
    this.callRemoveAll()
    this.updateSelectedSecuritiesList()
    this.updateComparisonResults()
    this.updateAddButton()

    // Hide the comparison results when clearing all
    const resultsContainer = this.comparisonResultsTarget
    resultsContainer.classList.add("hidden")
  }

  async callRemoveAll() {
    try {
      const formData = new FormData()

      const response = await fetch('/tools/add_compare_security', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)

        // Clear the comparison results
        const resultsContainer = this.comparisonResultsTarget
        resultsContainer.classList.add("hidden")
      }
    } catch (error) {
      console.error('Error clearing all securities:', error)
    }
  }

  updateSelectedSecuritiesList() {
    const container = this.selectedSecuritiesTarget
    container.innerHTML = ""

    if (this.selectedSecurities.length === 0) {
      return
    }

    // Create the securities list with checkboxes
    const securitiesList = document.createElement("div")
    securitiesList.className = "space-y-2"

    this.selectedSecurities.forEach(security => {
      const securityElement = document.createElement("div")
      securityElement.className = "flex items-center justify-between p-3 bg-neutral-50 rounded-md"
      securityElement.innerHTML = `
        <div class="flex items-center space-x-3">
          <input
            type="checkbox"
            id="security_${security.id}"
            name="security_ids[]"
            value="${security.id}"
            checked
            class="h-4 w-4 text-primary border-neutral-300 rounded focus:ring-primary"
          />
          <label for="security_${security.id}" class="text-sm font-medium text-neutral-900">${security.name}</label>
        </div>
        <button
          type="button"
          class="text-neutral-400 hover:text-neutral-600"
          data-action="click->security-comparison#removeSecurity"
          data-security-id="${security.id}"
        >
          <i class="fa fa-times"></i>
        </button>
      `
      securitiesList.appendChild(securityElement)
    })

    // Add the Compare Selected button
    const compareButton = document.createElement("div")
    compareButton.className = "mt-4"
    compareButton.innerHTML = `
      <button
        type="button"
        class="px-6 py-2 bg-primary text-white font-medium rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary border border-primary"
        data-action="click->security-comparison#compareSelected"
      >
        Compare Selected
      </button>
    `
    securitiesList.appendChild(compareButton)

    container.appendChild(securitiesList)
  }

  compareSelected() {
    // Prepare the checked securities
    const checkedSecurities = this.selectedSecurities.filter(security => {
      const checkbox = document.getElementById(`security_${security.id}`)
      // If checkbox exists and is checked, include it. If checkbox doesn't exist, include by default
      return checkbox ? checkbox.checked : true
    })

    if (checkedSecurities.length === 0) {
      alert("Please select at least one security to compare.")
      return
    }

    // Load comparison data with only checked securities
    this.loadComparisonData(checkedSecurities.map(s => s.id))
  }

  updateComparisonResults() {
    const resultsContainer = this.comparisonResultsTarget

    // Only show comparison results if there are selected securities
    if (this.selectedSecurities.length === 0) {
      resultsContainer.classList.add("hidden")
      return
    }
  }

  updateAddButton() {
    const addButton = this.addButtonTarget
    const hasSelection = this.getSelectedSecurityValue() && this.getSelectedSecurityValue() !== ""
    const canAdd = this.selectedSecurities.length < 5

    addButton.disabled = !hasSelection || !canAdd
  }

  securitySelectChanged(event) {
    this.updateAddButton()
  }

  securitySelected(event) {
    this.updateAddButton()
  }

  getSelectedSecurityValue() {
    const hiddenInput = this.securitySelectTarget.querySelector('[data-uikit--select-single-target="hiddenInput"]')
    if (hiddenInput) {
      return hiddenInput.value
    }

    // Fallback: try to prepare the value from the component's public method if available
    const selectController = this.application.getControllerForElementAndIdentifier(this.securitySelectTarget, 'uikit--select-single')
    if (selectController && typeof selectController.getSelectedValue === 'function') {
      return selectController.getSelectedValue()
    }

    return ""
  }

  // Helper method to get the selected text from the select component
  getSelectedSecurityText() {
    // Try to get the text from the selected text element
    const selectedTextElement = this.securitySelectTarget.querySelector('[data-uikit--select-single-target="selectedText"]')
    if (selectedTextElement) {
      return selectedTextElement.textContent.trim()
    }

    // Fallback: try to get from the component's public method if available
    const selectController = this.application.getControllerForElementAndIdentifier(this.securitySelectTarget, 'uikit--select-single')
    if (selectController && typeof selectController.getSelectedValue === 'function') {
      const selectedValue = selectController.getSelectedValue()
      // Find the option with this value to get the text
      const optionElement = this.securitySelectTarget.querySelector(`[data-uikit--select-single-target="option"][data-value="${selectedValue}"]`)
      if (optionElement) {
        return optionElement.dataset.label
      }
    }

    return ""
  }

  clearSecuritySelect() {
    const selectController = this.application.getControllerForElementAndIdentifier(this.securitySelectTarget, 'uikit--select-single')
    if (selectController && typeof selectController.clear === 'function') {
      selectController.clear()
    } else {
      const hiddenInput = this.securitySelectTarget.querySelector('[data-uikit--select-single-target="hiddenInput"]')
      if (hiddenInput) {
        hiddenInput.value = ""
        hiddenInput.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }
  }

  async loadComparisonData(securityIds = null) {
    const ids = securityIds || this.selectedSecurities.map(s => s.id)

    if (ids.length === 0) return

    try {
      const params = new URLSearchParams()
      params.append('security_ids', ids.join(','))
      const response = await fetch(`/tools/compare_selected?${params.toString()}`, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)

        const resultsContainer = this.comparisonResultsTarget
        resultsContainer.classList.remove("hidden")

        // Load "All" charts by default after a short delay to ensure DOM is updated
        setTimeout(() => {
          this.loadDefaultCharts()
        }, 100)
      }
    } catch (error) {
      console.error('Error loading comparison data:', error)
    }
  }

  loadDefaultCharts() {
    const checkedSecurities = this.selectedSecurities.filter(security => {
      const checkbox = document.getElementById(`security_${security.id}`)
      return checkbox && checkbox.checked
    })

    const securityIds = checkedSecurities.map(s => s.id)

    if (securityIds.length === 0) return

    this.loadPerformanceChartByTimeframe('all', securityIds)

    this.loadMarginChartByTimeframe('all', securityIds)
  }

  async loadPerformanceChartByTimeframe(timeframe, securityIds) {
    // Update button active states
    this.updateChartButtonStates('performance', timeframe)

    if (securityIds.length === 0) return

    try {
      // Build the URL with security_ids as comma-separated string
      const params = new URLSearchParams()
      params.append('security_ids', securityIds.join(','))
      params.append('time', timeframe)

      const response = await fetch(`/tools/comparison_graph?${params.toString()}`, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (response.ok) {
        const html = await response.text()

        Turbo.renderStreamMessage(html)
      }
    } catch (error) {
      console.error('Error loading performance chart:', error)
    }
  }

  async loadMarginChartByTimeframe(timeframe, securityIds) {
    this.updateChartButtonStates('margin', timeframe)

    if (securityIds.length === 0) return

    try {
      const params = new URLSearchParams()
      params.append('security_ids', securityIds.join(','))
      params.append('time', timeframe)

      const response = await fetch(`/tools/comparison_margin_graph?${params.toString()}`, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }
    } catch (error) {
      console.error('Error loading margin chart:', error)
    }
  }

  async loadPerformanceChart(event) {
    const timeframe = event.currentTarget.dataset.timeframe

    const checkedSecurities = this.selectedSecurities.filter(security => {
      const checkbox = document.getElementById(`security_${security.id}`)
      return checkbox && checkbox.checked
    })

    const securityIds = checkedSecurities.map(s => s.id)

    this.loadPerformanceChartByTimeframe(timeframe, securityIds)
  }

  async loadMarginChart(event) {
    const timeframe = event.currentTarget.dataset.timeframe

    const checkedSecurities = this.selectedSecurities.filter(security => {
      const checkbox = document.getElementById(`security_${security.id}`)
      return checkbox && checkbox.checked
    })

    const securityIds = checkedSecurities.map(s => s.id)

    this.loadMarginChartByTimeframe(timeframe, securityIds)
  }

  updateChartButtonStates(chartType, activeTimeframe) {
    // Find the chart container
    const chartContainer = document.querySelector(`#${chartType}-chart`).closest('.bg-white')

    // Find all timeframe buttons in this chart section
    const buttons = chartContainer.querySelectorAll('[data-action*="load' + (chartType === 'performance' ? 'Performance' : 'Margin') + 'Chart"]')

    buttons.forEach(button => {
      const timeframe = button.dataset.timeframe

      if (timeframe === activeTimeframe) {
        // Set active state
        button.classList.remove('bg-white', 'text-neutral-700', 'border-neutral-300')
        button.classList.add('bg-primary', 'text-white', 'border-primary')
      } else {
        // Set inactive state
        button.classList.remove('bg-primary', 'text-white', 'border-primary')
        button.classList.add('bg-white', 'text-neutral-700', 'border-neutral-300')
      }
    })
  }
}
